import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Trash, Package, Copy, ClipboardText, ArrowsClockwise, PencilRuler } from '@phosphor-icons/react';
import { ProductIcon, IconWithTransom } from './WindowIcons';
import { svgDataUri } from './DrawingEditorDialog';

// Input with local state - types instantly, syncs to parent on blur or after delay
const FastInput = React.memo(({ value, onChange, type, inputMode, ...props }) => {
  const [local, setLocal] = useState(value);
  const timerRef = useRef(null);

  useEffect(() => { setLocal(value); }, [value]);

  const handleChange = (e) => {
    const v = e.target.value;
    setLocal(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChange(v), 300);
  };

  const handleBlur = () => {
    clearTimeout(timerRef.current);
    if (local !== value) onChange(local);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Auto inputMode=decimal per type=number (tastiera numerica iOS)
  const autoInputMode = inputMode || (type === 'number' ? 'decimal' : undefined);
  return <Input type={type} inputMode={autoInputMode} value={local} onChange={handleChange} onBlur={handleBlur} {...props} />;
});
FastInput.displayName = 'FastInput';

// Same but for bare input element (product name)
const FastBareInput = React.memo(({ value, onChange, ...props }) => {
  const [local, setLocal] = useState(value);
  const timerRef = useRef(null);

  useEffect(() => { setLocal(value); }, [value]);

  const handleChange = (e) => {
    const v = e.target.value;
    setLocal(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChange(v), 300);
  };

  const handleBlur = () => {
    clearTimeout(timerRef.current);
    if (local !== value) onChange(local);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return <input value={local} onChange={handleChange} onBlur={handleBlur} {...props} />;
});
FastBareInput.displayName = 'FastBareInput';

const QuoteItem = React.memo(({
  item, index, updateItem, removeItem, duplicateItem, onIconChange, onNameChange, onChangeProduct, onEditDrawing,
  groupedProfiles, defaultColors, glassTypes, openingTypes,
  glazingBeadTypes, thresholdOptions, getProfileDescription, copyDescField, pasteDescField
}) => {
  const { t } = useTranslation();

  // Dynamic icon sizing based on product dimensions
  const getIconSize = () => {
    const w = parseFloat(item.width) || 1000;
    const h = parseFloat(item.height) || 1000;
    const area = w * h;
    // Base: 1000x1000 = 1.0, scale range 0.7 - 1.4
    const scale = Math.max(0.7, Math.min(1.4, Math.sqrt(area / 1000000)));
    const baseW = 48; // px (w-12)
    const baseH = 56; // px (h-14)
    return {
      width: Math.round(baseW * scale),
      height: Math.round(baseH * scale)
    };
  };
  const iconSize = getIconSize();

  return (
    <div className="border border-[#E2E8F0] rounded-sm p-4" data-testid={`quote-item-${index}`}>
      {/* Product Header */}
      <div className="flex items-start justify-between mb-4 pb-3 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-3 flex-1 min-w-0 mr-2">
          <div className="relative group">
            <button
              type="button"
              onClick={() => onChangeProduct(index)}
              className="bg-[#F4F6F8] rounded-sm p-1 hover:ring-2 hover:ring-[#0033A0]/30 transition-all cursor-pointer flex items-center justify-center"
              style={{ width: iconSize.width, height: iconSize.height }}
              title="Cambia prodotto"
              data-testid={`icon-change-btn-${index}`}
            >
              {item.drawing_svg ? (
                <img src={svgDataUri(item.drawing_svg)} alt="Disegno" className="w-full h-full object-contain" data-testid={`item-drawing-${index}`} />
              ) : item.icon ? (
                item.transom_type === 'con_sopraluce' || item.transom_type === 'con_sottoluce' ? (
                  <IconWithTransom type={item.icon} position={item.transom_type === 'con_sopraluce' ? 'top' : 'bottom'} className="w-full h-full" />
                ) : (
                  <ProductIcon type={item.icon} className="w-full h-full" />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#A0AEC0]">
                  <Package size={24} />
                </div>
              )}
              <div className="absolute inset-0 bg-[#0033A0]/0 group-hover:bg-[#0033A0]/10 rounded-sm transition-colors flex items-center justify-center">
                <ArrowsClockwise size={16} className="text-[#0033A0] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <FastBareInput
              type="text"
              value={item.product_type_name}
              onChange={(v) => onNameChange(index, v)}
              className="font-medium text-[#0A0F1A] bg-transparent border-0 border-b border-transparent hover:border-[#E2E8F0] focus:border-[#0033A0] focus:outline-none focus:ring-0 w-full px-0 py-0.5 text-sm transition-colors"
              data-testid={`product-name-input-${index}`}
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onEditDrawing && (
            <Button
              variant="ghost" size="sm"
              onClick={() => onEditDrawing(index)}
              className="text-[#0033A0] hover:bg-[#0033A0]/10"
              title={item.drawing ? 'Modifica il disegno' : 'Crea un disegno personalizzato'}
              data-testid={`drawing-btn-${index}`}
            >
              <PencilRuler size={16} />
            </Button>
          )}
          <Button
            variant="ghost" size="sm"
            onClick={() => duplicateItem(index)}
            className="text-[#0033A0] hover:bg-[#0033A0]/10"
            title="Duplica prodotto"
            data-testid={`duplicate-item-${index}`}
          >
            <Copy size={16} />
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => removeItem(index)}
            className="text-red-600 hover:bg-red-50"
            data-testid={`remove-item-${index}`}
          >
            <Trash size={16} />
          </Button>
        </div>
      </div>

      {/* Configuration Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">{t('quoteBuilder.widthMm')}</Label>
          <FastInput type="number" value={item.width} onChange={(v) => updateItem(index, 'width', v)} className="mt-1 h-9" data-testid={`width-${index}`} />
        </div>
        <div>
          <Label className="text-xs">{t('quoteBuilder.heightMm')}</Label>
          <FastInput type="number" value={item.height} onChange={(v) => updateItem(index, 'height', v)} className="mt-1 h-9" data-testid={`height-${index}`} />
        </div>
        <div>
          <Label className="text-xs">{t('quoteBuilder.quantity')}</Label>
          <FastInput type="number" min="1" value={item.quantity} onChange={(v) => updateItem(index, 'quantity', v)} className="mt-1 h-9" data-testid={`qty-${index}`} />
        </div>
        <div>
          <Label className="text-xs">Unità</Label>
          <Select value={item.unit_of_measure || '_none'} onValueChange={(v) => updateItem(index, 'unit_of_measure', v === '_none' ? '' : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`unit-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Nessuna</span></SelectItem>
              <SelectItem value="Pezzi">Pezzi</SelectItem>
              <SelectItem value="Kg">Kg</SelectItem>
              <SelectItem value="MQ">MQ</SelectItem>
              <SelectItem value="ML">ML</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">m²</Label>
          <Input value={item.area_m2?.toFixed(2) || '0'} readOnly className="mt-1 h-9 bg-[#F4F6F8]" />
        </div>

        <div>
          <Label className="text-xs">{t('quoteBuilder.profile')}</Label>
          <Select value={item.profile || '_none'} onValueChange={(v) => updateItem(index, 'profile', v === '_none' ? '' : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`profile-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Nessun profilo</span></SelectItem>
              {(groupedProfiles || []).map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.isCustom ? (
                    <span className="flex items-center gap-1.5">
                      <span className="text-[10px] text-[#4A5568]">{p.parentName} ›</span>
                      {p.name}
                    </span>
                  ) : p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">{t('quoteBuilder.color')}</Label>
          <Select value={item.color || '_none'} onValueChange={(v) => updateItem(index, 'color', v === '_none' ? '' : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`color-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Nessun colore</span></SelectItem>
              {(defaultColors || []).map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm border" style={{ backgroundColor: c.hex }} />
                    {c.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">{t('quoteBuilder.glass')}</Label>
          <Select value={item.glass_type || '_none'} onValueChange={(v) => updateItem(index, 'glass_type', v === '_none' ? '' : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`glass-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Nessun vetro</span></SelectItem>
              {(glassTypes || []).map(g => (<SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">{t('quoteBuilder.opening')}</Label>
          <Select value={item.opening_type} onValueChange={(v) => updateItem(index, 'opening_type', v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`opening-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {openingTypes.map(o => (<SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {item.opening_detail && (
            <p className="text-[10px] text-[#0033A0] mt-1 font-medium">{item.opening_detail}</p>
          )}
        </div>

        <div>
          <Label className="text-xs">{t('quoteBuilder.glazingBead')}</Label>
          <Select value={item.glazing_bead} onValueChange={(v) => updateItem(index, 'glazing_bead', v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`bead-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {glazingBeadTypes.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        {item.hasThresholdOption && (
          <div>
            <Label className="text-xs">{t('quoteBuilder.threshold')}</Label>
            <Select value={item.threshold || 'standard'} onValueChange={(v) => updateItem(index, 'threshold', v)}>
              <SelectTrigger className="mt-1 h-9" data-testid={`threshold-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {thresholdOptions.map(th => (<SelectItem key={th.id} value={th.id}>{th.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Profile Description */}
      {getProfileDescription(item.profile) && (
        <div className="mt-3 px-3 py-2 bg-[#F8FAFF] border border-[#E2E8F0] rounded-sm">
          <p className="text-xs text-[#4A5568]">
            <span className="font-medium text-[#0033A0]">{t('quoteBuilder.profileLabel')}</span>{' '}
            {getProfileDescription(item.profile)}
          </p>
        </div>
      )}

      {/* Descriptive Fields */}
      <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
        <p className="text-xs font-medium text-[#4A5568] mb-2">{t('quoteBuilder.productDescription')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {[1,2,3,4,5,6].map(n => (
            <div key={n} className="flex items-center gap-1">
              <FastInput
                placeholder={t('quoteBuilder.voce', { n })}
                value={item[`desc_${n}`] || ''}
                onChange={(v) => updateItem(index, `desc_${n}`, v)}
                className="h-8 text-xs flex-1"
                data-testid={`desc-${n}-${index}`}
              />
              <Button
                variant="ghost" size="sm"
                className="h-8 w-8 p-0 text-[#4A5568] hover:text-[#0033A0] hover:bg-[#F0F4FF] shrink-0"
                onClick={() => copyDescField(item[`desc_${n}`])}
                title={t('quoteBuilder.copyText')}
                data-testid={`copy-desc-${n}-${index}`}
              >
                <Copy size={14} />
              </Button>
              <Button
                variant="ghost" size="sm"
                className="h-8 w-8 p-0 text-[#4A5568] hover:text-[#0033A0] hover:bg-[#F0F4FF] shrink-0"
                onClick={() => pasteDescField(index, `desc_${n}`)}
                title="Incolla testo"
                data-testid={`paste-desc-${n}-${index}`}
              >
                <ClipboardText size={14} />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Price + IVA */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-3 border-t border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[#4A5568] whitespace-nowrap">IVA</Label>
          <FastInput
            type="number" min="0" max="100"
            value={item.iva ?? 22}
            onChange={(v) => updateItem(index, 'iva', v)}
            className="w-16 h-8 text-sm"
            data-testid={`iva-${index}`}
          />
          <span className="text-xs text-[#4A5568]">%</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <Label className="text-xs text-[#4A5568] whitespace-nowrap">{t('quoteBuilder.pricePerUnit')}</Label>
            <FastInput
              type="number" min="0" step="0.01"
              value={item.unit_price?.toFixed(2) || '0'}
              onChange={(v) => updateItem(index, 'unit_price_manual', v)}
              className={`w-24 h-8 text-sm font-medium ${item.price_override ? 'border-amber-400 bg-amber-50' : ''}`}
              data-testid={`unit-price-${index}`}
            />
          </div>
          {item.price_override && (
            <Button
              variant="ghost" size="sm"
              className="h-7 px-2 text-xs text-amber-600 hover:bg-amber-50"
              onClick={() => updateItem(index, '_resetPrice', true)}
              title={t('quoteBuilder.restorePrice')}
              data-testid={`reset-price-${index}`}
            >
              {t('quoteBuilder.listPrice')}
            </Button>
          )}
          <div className="text-lg font-bold text-[#0033A0]" data-testid={`total-price-${index}`}>
            €{item.total_price?.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
});

QuoteItem.displayName = 'QuoteItem';
export default QuoteItem;
